import React, { Dispatch, MouseEvent, ReactNode } from 'react';
import { IconButton, Modal as MaterialModal, Paper, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import IconRow from './IconRow';
import IconRowCell from './IconRowCell';
import IconCircle from './IconCircle';

interface Props {
  children: ReactNode;
  className?: string;
  sx?: SxProps<Theme>;
  open: boolean;
  setOpen: Dispatch<boolean>;
  title: string;
  titleIcon?: Icon;
  disableClose?: boolean;
  shortContent?: boolean;
  headerText?: string;
  isQuickRun?: boolean;
  onClose?: () => void;
  // Wider dialogs: `sx` can't override the base maxWidth (it's applied last).
  maxWidth?: string;
}

const Modal: React.FC<Props> = ({
  children, className, sx, open, setOpen, title, titleIcon, disableClose, shortContent, headerText, isQuickRun, onClose,
  maxWidth = '40rem',
}: Props) => {
  const handleClose = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setOpen(false);
    onClose?.();
  };

  return (
    <MaterialModal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-header"
      disableRestoreFocus
    >
      <Paper
        className={className}
        component="section"
        elevation={8}
        id="modal"
        sx={[...(Array.isArray(sx) ? sx : sx ? [sx] : []), (t) => ({
          borderRadius: `${t.shape.borderRadius}px`,
          display: 'flex',
          flexDirection: 'column',
          left: '50%',
          maxHeight: 'calc(100vh - 2rem)',
          maxWidth,
          outline: 0,
          overflowY: 'auto',
          position: 'absolute',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'calc(100vw - 2rem)',
          ...isQuickRun ? {
            minHeight: 'unset',
            padding: '2em',
          } : {
            minHeight: shortContent ? '18rem' : '20rem',
            padding: '1rem',
            [t.breakpoints.up('md')]: { padding: '2.5rem' },
          },
        })]}
      >
        <IconRow noIcon={!titleIcon} isHeader>
          {titleIcon && (
            <IconRowCell type="icon" style={{ alignSelf: 'center' }}>
              <IconCircle Icon={titleIcon} />
            </IconRowCell>
          )}
          <IconRowCell type="text">
            <Typography
              component="h4"
              variant="h4"
              id="modal-header"
            >
              {title}
            </Typography>
            {headerText && (<Typography>{headerText}</Typography>)}
          </IconRowCell>
          {!disableClose && (
            <IconRowCell type={headerText ? 'withHeader' : 'actions'}>
              <IconButton onClick={handleClose} data-title="close-button" size="large">
                <CloseIcon color="primary" />
              </IconButton>
            </IconRowCell>
          )}
        </IconRow>
        {children}
      </Paper>
    </MaterialModal>
  );
};

export default Modal;
